import {
  useInfiniteQuery,
  InfiniteData,
  UseInfiniteQueryOptions,
  keepPreviousData,
  useQuery,
  UseQueryOptions,
  UseMutationOptions,
  useQueryClient,
  useMutation,
} from '@tanstack/react-query';
import { api, TicketDto, TicketListDto, UpdateTicketDto } from '@sib/api';
import { CACHE_TIME, DEFAULT_PAGE } from '@sib/api/constants';
import { QueryKey } from '@sib/api/enums';
import { InferSearchParamsQueryTypeWithArraySorter } from '@sib/api/types';

export const TICKET_NOTE_MAX_LENGTH = 200;

export const useInfiniteTicketsList = (
  payload: InferSearchParamsQueryTypeWithArraySorter<
    typeof api.tickets.ticketsControllerGetTickets
  >,
  options?: Partial<
    UseInfiniteQueryOptions<TicketListDto, Error, InfiniteData<TicketListDto>>
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.Tickets,
      QueryKey.Operation.List,
      payload,
      QueryKey.Operation.Infinite,
    ],

    queryFn: async ({ pageParam }) => {
      const data = await api.tickets.ticketsControllerGetTickets({
        ...payload,
        page: pageParam as number,
      });

      return { ...data, newItems: 0 };
    },

    getNextPageParam: lastPage => {
      const hasNextPage = lastPage.hasNextPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    placeholderData: keepPreviousData,
    initialPageParam: payload?.page || DEFAULT_PAGE,
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });

export const useTicketsList = (
  payload: InferSearchParamsQueryTypeWithArraySorter<
    typeof api.tickets.ticketsControllerGetTickets
  >,
  options?: Partial<UseQueryOptions<TicketListDto, Error, TicketListDto>>
) =>
  useQuery({
    ...options,
    queryKey: [QueryKey.Resource.Tickets, QueryKey.Operation.List, payload],
    queryFn: async () => api.tickets.ticketsControllerGetTickets(payload),
    placeholderData: keepPreviousData,
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });

export const useGetOneTicket = (
  ticketId: string,
  options?: Partial<UseQueryOptions<TicketDto, Error, TicketDto>>
) =>
  useQuery({
    ...options,
    enabled: !!ticketId,
    queryKey: [QueryKey.Resource.Tickets, ticketId],
    queryFn: async () => api.tickets.ticketsControllerGetTicket(ticketId),
  });

export const useUpdateTicket = (
  options?: UseMutationOptions<TicketDto, Error, UpdateTicketDto>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Tickets, QueryKey.Operation.List],
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Tickets, variables.ticketId],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.TicketOrganizationGroup],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.TicketAssigneeGroup,
          QueryKey.Operation.List,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.TicketTypeGroup],
      });

      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.TicketFiles,
          variables.ticketId,
          QueryKey.Operation.List,
        ],
      });
      if (variables.projectId)
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.ProjectTickets, variables.projectId],
        });
      if (data.vendor?.id) {
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.InternalPortalVendors,
            QueryKey.Operation.One,
            data.vendor?.id,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.InternalPortalVendors,
            QueryKey.Operation.List,
          ],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.VendorTickets,
          QueryKey.Operation.List,
          data.vendor?.id,
        ],
      });

      options?.onSuccess?.(data, variables, context);
    },

    mutationFn: ({
      ticketId,
      ...payload
    }: UpdateTicketDto & { ticketId: string }) =>
      api.tickets.ticketsControllerUpdateTicket(ticketId, payload),
  });
};
